import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Add, Clear, FolderOpen, Refresh, Search } from '@mui/icons-material';
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';

import { PlanningClient } from '@@api';
import { useApiClient, useToastService } from '@@common';
import { GalleryHeader, GalleryStatistics } from '@@components';

export default function PlanningProjectListView() {
	const client = useApiClient(PlanningClient);
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const toast = useToastService();
	const [searchText, setSearchText] = useState('');
	const [createOpen, setCreateOpen] = useState(false);
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');

	const { data, isLoading, refetch } = useQuery({
		queryKey: ['planning', 'projects', searchText],
		queryFn: () => client.searchProjects(searchText.trim() || undefined, undefined, 0, 100),
	});

	const createMutation = useMutation({
		mutationFn: () => client.createProject({ name: name.trim(), description: description.trim() }),
		onSuccess: async (project) => {
			await queryClient.invalidateQueries({ queryKey: ['planning', 'projects'] });
			setCreateOpen(false);
			setName('');
			setDescription('');
			navigate(`/planning/projects/${project.id}`);
		},
		onError: (error) => toast.error(error instanceof Error ? error.message : 'Failed to create project.'),
	});

	const projects = data?.items ?? [];
	const activeProjectCount = projects.filter((project) => project.isActive).length;

	return (
		<Stack spacing={2} sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, p: (theme) => `${theme.layout.edgeGap}px`, overflow: 'auto' }}>
			<GalleryHeader
				title="Projects"
				subtitle="Plan and organize domain work across your projects."
				utilityActions={
					<Tooltip title="Refresh projects">
						<IconButton aria-label="Refresh projects" onClick={() => refetch()}>
							<Refresh />
						</IconButton>
					</Tooltip>
				}
				primaryAction={
					<Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
						Create project
					</Button>
				}
				statistics={
					<GalleryStatistics
						items={[
							{ label: 'Projects on this page', value: projects.length },
							{ label: 'Active', value: activeProjectCount },
							{ label: 'Inactive', value: projects.length - activeProjectCount },
						]}
					/>
				}
				search={
					<TextField
						size="small"
						placeholder="Search projects…"
						inputProps={{ 'aria-label': 'Search projects' }}
						value={searchText}
						onChange={(event) => setSearchText(event.target.value)}
						fullWidth
						InputProps={{
							startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
							endAdornment: searchText ? (
								<InputAdornment position="end">
									<IconButton aria-label="Clear project search" size="small" onClick={() => setSearchText('')}>
										<Clear fontSize="small" />
									</IconButton>
								</InputAdornment>
							) : undefined,
						}}
					/>
				}
			/>

			<Box sx={{ flex: 1, minHeight: 0 }}>
				{isLoading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
				) : data?.items.length ? (
					<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))', gap: 1.5 }}>
						{data.items.map((project) => (
							<Paper
								key={project.id}
								variant="outlined"
								component="button"
								onClick={() => navigate(`/planning/projects/${project.id}`)}
								sx={{
									p: 2,
									minHeight: 138,
									borderRadius: 1,
									bgcolor: 'background.paper',
									color: 'text.primary',
									textAlign: 'left',
									cursor: 'pointer',
									font: 'inherit',
									transition: 'border-color 120ms ease, background-color 120ms ease',
									'&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
								}}
							>
								<Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
									<FolderOpen color="primary" fontSize="small" />
									<Chip label={project.isActive ? 'Active' : 'Inactive'} color={project.isActive ? 'success' : 'default'} size="small" variant="outlined" />
								</Stack>
								<Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1.25 }}>{project.name}</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
									{project.description || 'No description'}
								</Typography>
							</Paper>
						))}
					</Box>
				) : (
					<Box sx={{ py: 8, textAlign: 'center' }}>
						<FolderOpen sx={{ fontSize: 42, color: 'text.disabled' }} />
						<Typography variant="body1" sx={{ mt: 1 }}>No planning projects found.</Typography>
						<Typography variant="body2" color="text.secondary">Create a project to begin gathering domain stories.</Typography>
					</Box>
				)}
			</Box>

			<Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle>Create Planning Project</DialogTitle>
				<DialogContent dividers>
					<Stack spacing={2} sx={{ pt: 0.5 }}>
						<TextField autoFocus required label="Name" value={name} onChange={(event) => setName(event.target.value)} />
						<TextField label="Description" value={description} onChange={(event) => setDescription(event.target.value)} multiline minRows={3} />
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCreateOpen(false)}>Cancel</Button>
					<Button variant="contained" onClick={() => createMutation.mutate()} disabled={!name.trim() || createMutation.isPending}>Create</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
}
